import { FormattedMessage } from "react-intl";
import React from "react";
import { GroupedTicketTypes, PriceMode } from "../../types";
import { AvailabilityState, SelectedState } from "@hooplab/ui-components/dist/types";
import confetti from "canvas-confetti";
import { CreateReservationError } from "../../dao/reservation";
import assertNever from "../../helpers/assertNever";

export const stateTexts: { [key: string]: React.ReactNode } = {
  NONE: null,

  PROMO: (
    <FormattedMessage id="components.Cart.TicketTypeRow.promoLabel" defaultMessage={"Promo"} />
  ),
  FEATURED: (
    <FormattedMessage
      id="components.Cart.TicketTypeRow.featuredLabel"
      defaultMessage={"Featured"}
    />
  ),
  PROMOTED: (
    <FormattedMessage
      id="components.Cart.TicketTypeRow.promotedLabel"
      defaultMessage={"Promoted"}
    />
  ),
};

export const availabilityTexts: { [key: string]: React.ReactNode } = {
  SOLD_OUT: (
    <FormattedMessage id="components.Cart.TicketTypeRow.soldOutLabel" defaultMessage={"Sold out"} />
  ),
  FEW_LEFT: (
    <FormattedMessage
      id="components.Cart.TicketTypeRow.fewLeftLabel"
      defaultMessage={"Few left!"}
    />
  ),
  MANY_LEFT: null,
  NO_RESERVABLE: (
    <FormattedMessage
      id="components.Cart.TicketTypeRow.fewLeftLabel"
      defaultMessage={"Few left!"}
    />
  ),
};

export const determineGroupAvailability = (
  groupedTicketType: GroupedTicketTypes,
  quantity: number,
): AvailabilityState => {
  // Check if all ticket types are sold out
  if (
    (groupedTicketType.ticketTypes.every(
      (ticketGroup) => ticketGroup.ticketType.availabilityStatus === "SOLD_OUT",
    ) ||
      groupedTicketType.ticketTypeGroup?.availabilityStatus === "SOLD_OUT") &&
    quantity === 0
  ) {
    return "SOLD_OUT";
  }

  if (
    groupedTicketType.ticketTypeGroup?.availabilityStatus === "FEW_LEFT" ||
    groupedTicketType.ticketTypeGroup?.availabilityStatus === "NO_RESERVABLE"
  ) {
    return groupedTicketType.ticketTypeGroup?.availabilityStatus;
  }

  // If none of the above conditions are met, return MANY_LEFT
  return "MANY_LEFT";
};

export const determineGroupState = (groupedTicketType: GroupedTicketTypes): SelectedState => {
  // Check for any ticket that is marked as secret, assuming 'secret' implies 'promo'
  if (
    groupedTicketType.ticketTypeGroup?.ticketTypeGroupId === -1 &&
    groupedTicketType.ticketTypes.every((ticketGroup) => ticketGroup.ticketType.isSecret)
  ) {
    return "PROMO";
  }

  if (
    groupedTicketType.ticketTypeGroup?.ticketTypeGroupId === -3 &&
    groupedTicketType.ticketTypes.every((ticketGroup) => ticketGroup.ticketType.isFeatured)
  ) {
    return "FEATURED";
  }

  if (
    groupedTicketType.ticketTypeGroup?.ticketTypeGroupId === -2 &&
    groupedTicketType.ticketTypes.every((ticketGroup) => ticketGroup.promoted)
  ) {
    return "PROMOTED";
  }

  // If none of the above conditions are met, return NONE
  return "NONE";
};

export const getTopError = (error: CreateReservationError, cancelCheckout: () => void) => {
  switch (error.type) {
    case "MaxCapacityExceededForEvent":
      return (
        <FormattedMessage
          id="components.Cart.capacityEventError"
          defaultMessage={"There are not enough available tickets to create your reservation."}
        />
      );
    case "IllegalSecretTicketReservation":
      return (
        <FormattedMessage
          id="components.Cart.illegalSecretTicket"
          defaultMessage={
            "Could not reserve ticket without a belonging promo code. Enter promo code and try again."
          }
        />
      );
    case "ReservationCheckoutLocked":
      return (
        <div className="flex flex-col gap-y-2 items-start">
          <FormattedMessage
            tagName={"span"}
            id="components.Cart.reservationCheckoutLocked"
            defaultMessage={
              "The reservation has an ongoing checkout. Please complete the checkout or cancel."
            }
          />
          <button
            onClick={() => cancelCheckout()}
            className="text-gray-800 underline hover:opacity-70"
          >
            <FormattedMessage
              id="screens.Event.cancelCheckout"
              defaultMessage={"Cancel checkout"}
            />
          </button>
        </div>
      );
    case "GenericReservationError":
      return (
        <FormattedMessage
          id="components.Cart.reservationError"
          defaultMessage={"An unknown error occured, please try again."}
        />
      );
    case "MaxAddonProductsPerEventExceeded":
      return null;
    case "MaxCapacityExceededForTicketType": {
      return null;
    }
    case "MaxCapacityExceededForTicketTypeGroup": {
      return null;
    }
    case "CouldNotAllocateTicket":
      return null;
    case "CouldNotAllocateAddon":
      return null;
    case "ReservationNotFound":
      return null;
    default:
      assertNever(error);
  }
};

export const getTicketError = (
  error: CreateReservationError,
): { id: number; message: React.ReactNode } | null => {
  switch (error.type) {
    case "MaxCapacityExceededForTicketTypeGroup":
    case "MaxCapacityExceededForTicketType":
      return {
        id: error.ticketTypeId,
        message: (
          <FormattedMessage
            id="components.Cart.TicketTypeRow.notEnoughLeft"
            defaultMessage={
              "Unfortunately, there are not enough of this ticket type left to complete the reservation."
            }
          />
        ),
      };
    case "CouldNotAllocateTicket":
      return {
        id: error.ticketTypeId,
        message: (
          <FormattedMessage
            id="components.Cart.TicketTypeRow.unableToAllocate"
            defaultMessage={
              "We could not reserve this ticket. This could mean that the ticket is no longer for sale."
            }
          />
        ),
      };
    default:
      return null;
  }
};

export const checkExceedsMaxEvent = (maxPerPurchase: number, count: number) =>
  maxPerPurchase < count;

export const checkExceedsMax = (maxPerTicket: number | null, count: number) =>
  maxPerTicket && maxPerTicket < count;

export const checkLessThanMin = (minPerTicket: number | null, count: number) =>
  minPerTicket && minPerTicket > count && count !== 0;

export const checkNotDivisibleBy = (mod: number | null, count: number) =>
  mod && count && count % mod !== 0;

export const renderPriceModeText = (priceMode: PriceMode) => {
  switch (priceMode) {
    case "PRIVATE/NO_FEE":
    case "PRICE_MODE_EXCLUDE_FEE":
    case "BUSINESS/NO_FEE":
    case "PRICE_MODE_EXCLUDE_FEE_AND_VAT":
      return (
        <FormattedMessage
          id={"components.TicketBox.renderPriceModeText.excludeTicketFees"}
          defaultMessage={"Prices exclude ticket fees"}
        />
      );
    case "PRIVATE/SHOW_FEE":
    case "PRICE_MODE_DEFAULT":
    case "BUSINESS/SHOW_FEE":
    case "PRICE_MODE_EXCLUDE_VAT":
    case "PRIVATE/SPECIFIED_FEE":
    case "BUSINESS/SPECIFIED_FEE":
    default:
      return (
        <FormattedMessage
          id={"components.TicketBox.renderPriceModeText.includeTicketFees"}
          defaultMessage={"Prices include ticket fees"}
        />
      );
  }
};

var colors = ["#965afa", "#6391e8"];

export const confetti2 = () => {
  confetti({
    particleCount: 150,
    angle: 60,
    spread: 55,
    ticks: 100,
    origin: { x: 0 },
    colors: colors,
  });
  confetti({
    particleCount: 150,
    angle: 120,
    spread: 55,
    ticks: 100,
    origin: { x: 1 },
    colors: colors,
  });
};
